import { useState } from "react";
import type { IncidentCard } from "@/mocks/gameData";

interface Props {
  card: IncidentCard;
  onResolve: (correct: boolean) => void;
}

export default function IncidentCardModal({ card, onResolve }: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);

  const handleSelect = (idx: number) => {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
  };

  const handleContinue = () => {
    onResolve(selected === card.correctIndex);
  };

  const isCorrect = selected === card.correctIndex;
  const optionLabels = ["A", "B", "C", "D"];

  const getOptionStyle = (idx: number) => {
    if (!answered) {
      return {
        border: "1px solid rgba(0,217,255,0.15)",
        background: "rgba(0,217,255,0.03)",
        cursor: "pointer",
        opacity: 1,
      };
    }
    if (idx === card.correctIndex) {
      return {
        border: "1px solid rgba(0,255,136,0.5)",
        background: "rgba(0,255,136,0.06)",
        cursor: "default",
        opacity: 1,
      };
    }
    if (idx === selected && selected !== card.correctIndex) {
      return {
        border: "1px solid rgba(255,68,68,0.5)",
        background: "rgba(255,68,68,0.06)",
        cursor: "default",
        opacity: 1,
      };
    }
    return {
      border: "1px solid rgba(255,255,255,0.04)",
      background: "transparent",
      cursor: "default",
      opacity: 0.35,
    };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />

      <div
        className="relative z-10 w-full max-w-2xl rounded-xl overflow-hidden flex flex-col"
        style={{
          background: "#0d0d0d",
          border: "1px solid rgba(0,217,255,0.3)",
          boxShadow: "0 0 40px rgba(0,217,255,0.08), 0 0 0 1px rgba(0,217,255,0.05)",
          maxHeight: "88vh",
        }}
      >
        {/* Alert header bar */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-5 py-3"
          style={{ background: "rgba(0,217,255,0.06)", borderBottom: "1px solid rgba(0,217,255,0.15)" }}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#ff4444] animate-pulse"></span>
              <span className="w-2 h-2 rounded-full bg-[#f59e0b]"></span>
              <span className="w-2 h-2 rounded-full bg-[#00ff88]"></span>
            </div>
            <div className="h-4 w-px bg-white/10"></div>
            <span className="text-[10px] text-[#00d9ff]/70 font-mono uppercase tracking-widest font-bold">
              INCIDENT_CARD · SQ_{String(card.square).padStart(3, "0")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-[9px] font-bold font-mono px-2 py-0.5 rounded"
              style={{ background: "rgba(255,68,68,0.15)", color: "#ff4444", border: "1px solid rgba(255,68,68,0.3)" }}
            >
              ACTIVE INCIDENT
            </span>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Scenario */}
          <div className="px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <div className="flex items-start gap-3">
              <div
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md mt-0.5"
                style={{ background: "rgba(0,217,255,0.1)", border: "1px solid rgba(0,217,255,0.25)" }}
              >
                <i className="ri-alert-line text-[#00d9ff] text-sm"></i>
              </div>
              <div>
                <div className="text-[10px] text-white/30 font-mono uppercase tracking-widest mb-1.5">Scenario</div>
                <p className="text-sm text-white/65 leading-relaxed font-mono">{card.scenario}</p>
              </div>
            </div>
          </div>

          {/* Question */}
          <div className="px-6 py-5">
            <div className="text-[10px] text-white/30 font-mono uppercase tracking-widest mb-3">
              &gt; What is the correct action?
            </div>
            <p className="text-base font-bold text-white mb-4 leading-snug">{card.question}</p>

            <div className="flex flex-col gap-2">
              {card.options.map((opt, idx) => {
                const style = getOptionStyle(idx);
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    className="w-full flex items-start gap-3 px-4 py-3 rounded-lg text-left transition-all group"
                    style={style}
                    onMouseEnter={(e) => {
                      if (!answered) {
                        (e.currentTarget as HTMLElement).style.borderColor = "rgba(0,217,255,0.4)";
                        (e.currentTarget as HTMLElement).style.background = "rgba(0,217,255,0.07)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!answered) {
                        (e.currentTarget as HTMLElement).style.borderColor = "rgba(0,217,255,0.15)";
                        (e.currentTarget as HTMLElement).style.background = "rgba(0,217,255,0.03)";
                      }
                    }}
                  >
                    <span
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-[11px] font-bold font-mono mt-0.5"
                      style={{
                        background:
                          answered && idx === card.correctIndex
                            ? "rgba(0,255,136,0.2)"
                            : answered && idx === selected && !isCorrect
                            ? "rgba(255,68,68,0.2)"
                            : "rgba(0,217,255,0.1)",
                        color:
                          answered && idx === card.correctIndex
                            ? "#00ff88"
                            : answered && idx === selected && !isCorrect
                            ? "#ff4444"
                            : "#00d9ff",
                        border:
                          answered && idx === card.correctIndex
                            ? "1px solid rgba(0,255,136,0.3)"
                            : answered && idx === selected && !isCorrect
                            ? "1px solid rgba(255,68,68,0.3)"
                            : "1px solid rgba(0,217,255,0.2)",
                      }}
                    >
                      {answered && idx === card.correctIndex ? (
                        <i className="ri-check-line text-xs"></i>
                      ) : answered && idx === selected && !isCorrect ? (
                        <i className="ri-close-line text-xs"></i>
                      ) : (
                        optionLabels[idx]
                      )}
                    </span>
                    <span className="text-sm text-white/70 leading-relaxed">{opt}</span>
                  </button>
                );
              })}
            </div>

            {/* Result + explanation */}
            {answered && (
              <div
                className="mt-5 rounded-lg overflow-hidden"
                style={{ border: `1px solid ${isCorrect ? "rgba(0,255,136,0.2)" : "rgba(255,68,68,0.2)"}` }}
              >
                {/* Result banner */}
                <div
                  className="flex items-center gap-3 px-4 py-2.5"
                  style={{
                    background: isCorrect ? "rgba(0,255,136,0.08)" : "rgba(255,68,68,0.08)",
                    borderBottom: `1px solid ${isCorrect ? "rgba(0,255,136,0.15)" : "rgba(255,68,68,0.15)"}`,
                  }}
                >
                  <i
                    className={`${isCorrect ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-base`}
                    style={{ color: isCorrect ? "#00ff88" : "#ff4444" }}
                  ></i>
                  <span
                    className="text-xs font-bold font-mono uppercase tracking-wider"
                    style={{ color: isCorrect ? "#00ff88" : "#ff4444" }}
                  >
                    {isCorrect ? "Correct — Moving Forward" : "Incorrect — Falling Back"}
                  </span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <i
                      className={`${isCorrect ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-sm`}
                      style={{ color: isCorrect ? "#00ff88" : "#ff4444" }}
                    ></i>
                    <span
                      className="text-sm font-black font-mono"
                      style={{ color: isCorrect ? "#00ff88" : "#ff4444" }}
                    >
                      {isCorrect ? `+${card.reward}` : `-${card.penalty}`} sq
                    </span>
                  </div>
                </div>

                {/* Explanation */}
                <div className="px-4 py-3" style={{ background: "rgba(255,255,255,0.02)" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-book-open-line text-[#f59e0b] text-xs"></i>
                    <span className="text-[10px] text-[#f59e0b]/80 font-mono uppercase tracking-widest font-bold">Postmortem Note</span>
                  </div>
                  <p className="text-[12px] text-white/55 leading-relaxed font-mono">{card.explanation}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pinned footer — always visible */}
        {answered && (
          <div
            className="flex-shrink-0 px-6 py-4"
            style={{ borderTop: "1px solid rgba(255,255,255,0.08)", background: "#0d0d0d" }}
          >
            <button
              onClick={handleContinue}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
              style={{
                background: isCorrect ? "#00ff88" : "rgba(255,68,68,0.8)",
                color: isCorrect ? "#0a0a0a" : "#fff",
              }}
            >
              <i className="ri-arrow-right-line"></i>
              {isCorrect ? "Continue — Advance Position" : "Continue — Accept Penalty"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
